import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from 'react-router-dom';
import { ClinicianRoutePrefix, paths, Routes } from 'routes';

import { PrivateRoute } from 'navigation/router/components/routes/private-route';

import { Role } from 'features/auth/types';

import { Alert, Flex, Skeleton, Spin } from 'antd';
import { CRX_SUPPORT_EMAIL } from 'app/config';
import { ErrorDisplay, ErrorListDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { useAuth } from 'lib/hooks/auth/use-auth';
import { useGetBasicClinicInfo } from 'lib/hooks/tanstack/use-getClinicInfo';
import { useHasPaymentMethod } from 'lib/hooks/tanstack/use-getProductPrice';
import {
  ClinicianContext,
  useCreateClinicianContextState
} from 'lib/hooks/use-resolveCurrentClinicInfo';
import { useErrorList } from 'lib/utils/useErrorList';
import { Check } from 'lucide-react';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { CLINICIAN_ROUTES } from '../config/routes-clinician';
import { ClinicContextForm } from './form-clinic-context';
import { OnboardingRequiredDialog } from './onboarding-required-dialog';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';

export function ClinicianRouter() {
  return (
    <Switch>
      <Route exact path={ClinicianRoutePrefix}>
        <Redirect to={Routes.ClinicianHome} />
      </Route>

      {CLINICIAN_ROUTES.map(config => (
        <PrivateRoute
          key={`clinicianroutesmap-${config.path}`}
          roles={config.roles ?? [Role.Clinician, Role.ClinicAdmin]}
          path={config.path}
          component={config.component}
          redirectPath={config.redirectPath ?? paths.signIn()}
          exact={config.exact}
        />
      ))}
    </Switch>
  );
}

interface ClinicianRouteSwitchChildProps {
  clinicId: string;
  shouldShowOnboarding: boolean;
}

function ClinicianRouteSwitchChild({
  clinicId,
  shouldShowOnboarding,
  children
}: PropsWithChildren<ClinicianRouteSwitchChildProps>) {
  const history = useHistory();
  const errorList = useErrorList();

  const hasPaymentMethodQuery = useHasPaymentMethod(clinicId);
  const currentClinicInfo = useGetBasicClinicInfo(clinicId);

  useQueryErrorNotifier(hasPaymentMethodQuery, errorList);
  useQueryErrorNotifier(currentClinicInfo, errorList);

  useEffect(() => {
    if (
      clinicId &&
      currentClinicInfo.isFetched &&
      !currentClinicInfo.data?.initialised
    ) {
      console.debug('Attempting push to onboarding setup');
      history.push(`/onboarding/setup/${clinicId}`);
    }
  }, [clinicId, currentClinicInfo.isFetched, currentClinicInfo.data, history]);

  if (errorList.hasErrors) {
    return <ErrorListDisplay errorList={errorList} />;
  }

  if (hasPaymentMethodQuery.isLoading || currentClinicInfo.isLoading) {
    return (
      <div>
        <Text>Loading organisation data...</Text>
        <Text>
          Organization information:{' '}
          {currentClinicInfo.isLoading ? <Spin /> : <Check />}
        </Text>
        <Text>
          Payment information:{' '}
          {hasPaymentMethodQuery.isLoading ? <Spin /> : <Check />}
        </Text>
        <Skeleton active />
      </div>
    );
  }

  return (
    <>
      {shouldShowOnboarding && <OnboardingRequiredDialog />}
      {children}
    </>
  );
}

export function ClinicianRouteSwitchWrapper(
  props: PropsWithChildren<{ clinicianId: string }>
) {
  const clinicianInfoState = useCreateClinicianContextState(props.clinicianId);
  const errorList = useErrorList();
  const history = useHistory();
  const user = useAuth();
  const location = useLocation();

  /**
   * Add critical errors to the error list when they are available
   */
  useEffect(
    () => {
      if (clinicianInfoState.hasErrors) {
        errorList.reset();
        clinicianInfoState.errors.forEach(_error => {
          const error = new Error(`[ClinicInfo]: ${_error.message}`);

          return errorList.addError(error);
        });
      }
    },
    // eslint-disable-next-line
    [clinicianInfoState.errors]
  );

  /**
   * Reset the route when the clinic information changes
   */
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (clinicianInfoState.currentClinic !== '') {
      history.push(ClinicianRoutePrefix);
    }
  }, [clinicianInfoState.currentClinic, history]);

  /**
   * Show a critical error when the Clinic data could not be loaded
   */
  if (errorList.hasErrors) {
    return <ErrorListDisplay errorList={errorList} />;
  }

  if (
    clinicianInfoState.isFetched &&
    clinicianInfoState.clinicIds.length === 0
  ) {
    return (
      <div>
        <Text>You are not assigned to an organisation.</Text>
        <ErrorDisplay
          title="Not assigned to an organisation"
          description={`Please contact ${CRX_SUPPORT_EMAIL}`}
          error={{ message: 'No organisation' }}
        />
      </div>
    );
  }

  /**
   * Show a loading animation while the Clinic data is loading
   */
  if (!clinicianInfoState.isFetched) {
    return (
      <div>
        <Text>Loading organisation data...</Text>
        <Text>
          Customer information: <Spin />
        </Text>
        <Skeleton active />
      </div>
    );
  }

  if (clinicianInfoState.clinicNames.length === 0) {
    return (
      <div>
        <Alert
          type="warning"
          message={<Text>You are not assigned to any organisations</Text>}
        />
      </div>
    );
  }

  const shouldShowOnboarding =
    (user.user?.role === Role.ClinicAdmin ||
      user.user?.role === Role.GlobalAdmin ||
      user.user?.extraRole === Role.GlobalAdmin) &&
    location.pathname !== paths.clinician.billingSettings() &&
    location.pathname !== paths.clinician.billingSubscription();

  return (
    <ClinicianContext value={clinicianInfoState}>
      <Flex gap={16} vertical>
        <ClinicContextForm />

        {clinicianInfoState.currentClinic ? (
          <ClinicianRouteSwitchChild
            clinicId={clinicianInfoState.currentClinic}
            shouldShowOnboarding={shouldShowOnboarding}
          >
            {props.children}
          </ClinicianRouteSwitchChild>
        ) : (
          <div
            style={{
              marginTop: '2rem'
            }}
          >
            <Alert message="Please select an organisation" />
          </div>
        )}
      </Flex>
    </ClinicianContext>
  );
}
